import { useState, useEffect, useCallback, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import bs58 from 'bs58'
import { api } from '~/services/api'
import { AuthResponse, Profile, User } from '~/types/api'

// Account info type for backward compatibility
interface AccountInfo {
  address: string | null
  publicKey: PublicKey | null
  github: {
    name: string
    avatar: string
  } | null
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  accessToken: string | null
  refreshToken: string | null
}

export const useAccount = () => {
  const { publicKey, signMessage, connected } = useWallet()
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Clear stored authentication data
  const clearStoredAuth = useCallback(() => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
    setAuthState({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
    })
  }, [])

  // Load stored auth data on mount
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedUser = localStorage.getItem('user_data')

    if (storedAccessToken && storedRefreshToken && storedUser && connected) {
      try {
        const userData = JSON.parse(storedUser)
        // Only restore auth if the stored wallet matches the connected wallet
        if (userData.solanaWalletAddress === publicKey?.toBase58()) {
          setAuthState({
            isAuthenticated: true,
            user: userData,
            accessToken: storedAccessToken,
            refreshToken: storedRefreshToken,
          })
        } else {
          console.log('Stored wallet does not match connected wallet, clearing auth')
          clearStoredAuth()
        }
      } catch (err) {
        console.error('Failed to parse stored user data:', err)
        clearStoredAuth()
      }
    }
  }, [connected, publicKey, clearStoredAuth])

  // Store authentication data
  const storeAuthData = useCallback((authResponse: AuthResponse) => {
    localStorage.setItem('access_token', authResponse.accessToken)
    localStorage.setItem('refresh_token', authResponse.refreshToken)
    localStorage.setItem('user_data', JSON.stringify(authResponse.user))
    
    setAuthState({
      isAuthenticated: true,
      user: authResponse.user,
      accessToken: authResponse.accessToken,
      refreshToken: authResponse.refreshToken,
    })
  }, [])

  // Login with wallet signature
  const login = useCallback(async () => {
    if (!publicKey || !signMessage) {
      throw new Error('Wallet not connected or does not support message signing')
    }

    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Request challenge
      const { message } = await api.auth.challenge(publicKey.toBase58())

      // Step 2: Sign the challenge message
      const messageBytes = new TextEncoder().encode(message)
      const signature = await signMessage(messageBytes)
      // Convert signature to base58 as required by API
      const signedMessage = bs58.encode(signature)

      // Step 3: Login with signed message
      const authResponse = await api.auth.login({
        walletAddress: publicKey.toBase58(),
        signedMessage,
      })

      // Step 4: Store authentication data
      storeAuthData(authResponse)

      return authResponse
    } catch (err) {
      console.error('Login failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Login failed'
      setError(errorMessage)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [publicKey, signMessage, storeAuthData])

  // Logout
  const logout = useCallback(async () => {
    if (authState.refreshToken) {
      try {
        await api.auth.logout(authState.refreshToken)
      } catch (err) {
        console.error('Logout API call failed:', err)
        // Continue with local logout even if API call fails
      }
    }
    
    clearStoredAuth()
  }, [authState.refreshToken, clearStoredAuth])

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    if (!authState.refreshToken) {
      throw new Error('No refresh token available')
    }

    try {
      const authResponse = await api.auth.refreshToken(authState.refreshToken)
      storeAuthData(authResponse)
      return authResponse.accessToken
    } catch (err) {
      console.error('Token refresh failed:', err)
      clearStoredAuth()
      throw err
    }
  }, [authState.refreshToken, storeAuthData, clearStoredAuth])

  // Check if user has GitHub connected
  const checkGitHubConnection = useCallback(async () => {
    if (!authState.isAuthenticated) return false
    
    try {
      const profile = await api.profile.get()
      return profile.isGithubConnected
    } catch (err) {
      console.error('Failed to check GitHub connection:', err)
      return false
    }
  }, [authState.isAuthenticated])

  // Auto-logout when wallet disconnects
  useEffect(() => {
    if (!connected && authState.isAuthenticated) {
      clearStoredAuth()
    }
  }, [connected, authState.isAuthenticated, clearStoredAuth])

  // Create backward-compatible account object
  const account = useMemo<AccountInfo>(() => {
    if (connected && publicKey) {
      const github = authState.user?.githubId ? {
        name: authState.user.githubId,
        avatar: `https://github.com/${authState.user.githubId}.png?size=40`
      } : null

      return {
        address: publicKey.toBase58(),
        publicKey: publicKey,
        github,
      }
    } else {
      return {
        address: null,
        publicKey: null,
        github: null,
      }
    }
  }, [connected, publicKey, authState.user])

  return {
    // Backward compatibility - existing useAccount interface
    account,
    
    // New auth functionality - same as useAuth
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    accessToken: authState.accessToken,
    refreshToken: authState.refreshToken,
    login,
    logout,
    refreshAccessToken,
    checkGitHubConnection,
    isLoading,
    error,
    clearError: () => setError(null),
  }
}

